import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { RouteComponentProps } from "react-router";

import { analyticsStoreEvent } from "@/mk2/actions";
import {
    COMMON_PLACE_CATEGORY_SLUG,
    CONTESTS_TOPIC_ID,
    COUNSELLING_CATEGORY_ID,
    DAYS_WITH_CATEGORY_ID,
    TESTINGS_TOPIC_ID,
} from "@/mk2/apps/forum/constants";
import { topicUrl } from "@/mk2/apps/forum/helpers";
import { CategoryEntity, CategorySchema, TopicEntity, TopicSchema } from "@/mk2/apps/forum/schemas";
import { breadcrumbsForum } from "@/mk2/apps/forum/structuredData";
import {
    forumCommonPlaceForeignTopicsUrl,
    forumCommonPlaceTopicsUrl,
    forumTopicsUrl,
} from "@/mk2/apps/forum/urls";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { UserEntity } from "@/mk2/schemas";
import { findDenormalizedEntityBySlug, getDenormalizedEntities } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_DESCRIPTION_FORUM_category,
    SEO_TITLE_FORUM_TOPICS_OF_category,
} from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import { CommonPlaceTopics } from "./CommonPlaceTopics";
import {
    PlaceType,
    commonPlaceTopicsLoad,
    commonPlaceTopicsSearch,
    commonPlaceTopicsUpdateSearch,
} from "./CommonPlaceTopics.actions";
import commonPlaceTopicsPageReducer, {
    CommonPlaceTopicsPageState,
    SearchState,
} from "./CommonPlaceTopics.reducers";
import forumTopicsSaga from "./CommonPlaceTopics.sagas";
import { getForumTopicsSearchState, getForumTopicsState } from "./CommonPlaceTopics.selectors";

interface OwnProps {
    // You can change list item to="" link
    itemToTopicUrl(item: TopicEntity);
}

interface StateProps {
    after: number;
    category?: CategoryEntity;
    baseUrl: string;
    countriesRowNo: number;
    errorMessage: string;
    hasNext: number;
    hasPrevious: number;
    items: TopicEntity[];
    isMobile: boolean;
    loadingState: LoadingState;
    placeType: PlaceType;
    queryFromUrl: string;
    queryFromState: string;
    requestUser: UserEntity;
    requestUserPermissions: string[];
}

interface DispatchProps {
    onLoad(placeType: PlaceType, search: string, after?: number);
    onSearch(placeType: PlaceType, search: string);
    onUpdateSearch(search: string);
    onStoreEvent(name: string, props: any);
}

type RouteProps = RouteComponentProps<{}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class CommonPlaceTopicsPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    protected createTopic: boolean = true;

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const categorySlug = COMMON_PLACE_CATEGORY_SLUG;
            const category: CategoryEntity = findDenormalizedEntityBySlug<CategoryEntity>(
                state,
                CategorySchema,
                categorySlug,
            );
            const parsed = parse(ownProps.location.search, { parseNumbers: true });
            const queryFromUrl = (parsed.search as string) || "";
            const after = parsed.after as number;

            const placeType =
                ownProps.match.path === forumCommonPlaceForeignTopicsUrl ? "countries" : "counties";

            const pageState: CommonPlaceTopicsPageState = getForumTopicsState(state);
            const searchState: SearchState = getForumTopicsSearchState(
                state,
                placeType,
                queryFromUrl,
            );

            let items;
            let hasNext;
            if (this.createTopic) {
                items = getDenormalizedEntities<TopicEntity>(state, TopicSchema, searchState.ids);
                hasNext = searchState.hasNext;
            } else {
                const itemsAll = getDenormalizedEntities<TopicEntity>(
                    state,
                    TopicSchema,
                    searchState.ids,
                );
                items = itemsAll
                    .filter((item) => item.id !== CONTESTS_TOPIC_ID)
                    .filter((item) => item.id !== TESTINGS_TOPIC_ID)
                    .filter((item) => item.category.id !== COUNSELLING_CATEGORY_ID)
                    .filter((item) => item.category.id !== DAYS_WITH_CATEGORY_ID);

                // when the count of itemsAll is decreased by filtering, the hasNext value is not aligned with
                // the count of items. in these cases we set hasNext to zero
                hasNext = itemsAll.length !== items.length ? 0 : searchState.hasNext;
            }

            const requestUser = getRequestUser(state);
            const requestUserPermissions = getRequestPermissions(state);

            const countriesRowNo =
                placeType === "counties" &&
                !queryFromUrl &&
                !hasNext &&
                isSuccess(searchState.loadingState)
                    ? items.length
                    : null;

            return {
                after,
                baseUrl: getRequestBaseUrl(state),
                category,
                countriesRowNo,
                errorMessage: searchState.errorMessage,
                hasNext,
                hasPrevious: searchState.hasPrevious,
                isMobile: getRequestDeviceMobile(state),
                items,
                loadingState: searchState.loadingState,
                placeType,
                queryFromState: pageState.search, // Value in the input field
                queryFromUrl, // Value in the URL
                requestUser,
                requestUserPermissions,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: commonPlaceTopicsLoad,
            onSearch: commonPlaceTopicsSearch,
            onUpdateSearch: commonPlaceTopicsUpdateSearch,
            onStoreEvent: analyticsStoreEvent,
        };
    }

    public getMetaTitle(props: Props): string {
        const { category } = props;
        return interpolate(SEO_TITLE_FORUM_TOPICS_OF_category, {
            category: category ? category.name : "...",
        });
    }

    public getMetaDescription(props: Props): string {
        const { category } = props;

        return interpolate(SEO_DESCRIPTION_FORUM_category, {
            category: category ? category.name : "...",
        });
    }

    public getStructuredData(props: Props): object[] {
        const { category, baseUrl } = props;

        if (category) {
            return [breadcrumbsForum(baseUrl, category)];
        }
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        const { location } = props;
        const qs = parse(location.search);
        return baseUrl + url(forumCommonPlaceTopicsUrl, {}, { search: qs.search });
    }

    public render(props: Props): false | JSX.Element {
        return <CommonPlaceTopics {...props} itemToTopicUrl={this.itemToTopic} />;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return forumTopicsUrl;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Forum Category - Halfpage Sticky (Desktop)",
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: "Forum Category - Branding",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Forum Category - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { requestUser, isMobile, items } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if ((items?.length || 0) < 10) {
            return null;
        }

        return {
            zone: "Forum Category - Anchor - Mobile",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.forum.commonPlaceTopics", commonPlaceTopicsPageReducer);
        store.injectSaga("containers.forum.commonPlaceTopics", forumTopicsSaga);
    }

    private itemToTopic = (item: TopicEntity) => topicUrl(item);
}
